import React from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { directHoverGlobalSelector } from '../../styles/mixins';
import HBClickable from '../HBClickable/HBClickable';
import type { HBTableRowProps } from './HBTable.types';
import HBTableCell from './HBTableCell';
import { TABLE_ROW_BG_COLOR_VAR, TABLE_Z_INDEX } from './HBTable.styles';
import { inertAttr } from '../../utils/shared.utils';

function HBTableRow<T>({ table, row, isLoading }: HBTableRowProps<T>) {
    const { onRowClick, helper } = table.options.meta;

    return (
        <TR
            // I want rows to be focusable even if there's no onRowClick so that you could still navigate the the rows using the keyboard
            tag="tr"
            inert={isLoading}
            $isClickable={helper.isRowClickable}
            data-is-selected={helper.isRowSelected(row)}
            onClick={e => {
                if (
                    helper.isRowClickable &&
                    (e.currentTarget === document.activeElement ||
                        e.currentTarget.matches(directHoverGlobalSelector))
                ) {
                    if (e.key === ' ') {
                        // If space was pressed to toggle row selection, prevent it from scrolling the page
                        e.preventDefault();
                    }
                    if (onRowClick) {
                        onRowClick(row.original);
                    } else if (helper.hasRowSelection) {
                        helper.toggleTableRowSelection(row);
                    }
                }
            }}
        >
            {table.getAllColumns().map(column => (
                <HBTableCell
                    table={table}
                    key={column.id}
                    column={column}
                    row={row}
                    isLoading={isLoading}
                />
            ))}
        </TR>
    );
}

export default HBTableRow;

// -------------------------- Styles -------------------------- //

const TR = styled(HBClickable).attrs(inertAttr)<{ $isClickable: boolean }>`
    && {
        ${TABLE_ROW_BG_COLOR_VAR}: ${vars.colors.surface.container};
        background-color: var(${TABLE_ROW_BG_COLOR_VAR});
        transition: background-color 60ms;

        opacity: 1;

        &[inert] {
            transition: opacity 100ms;
            opacity: 0.3;
        }

        &:not(:last-child) {
            border-bottom: 1px solid ${vars.colors.stroke.tertiary};
        }

        &:focus-visible {
            // This ensures that the focus state is visible
            position: relative;
            z-index: ${TABLE_Z_INDEX.MIDDLE};
        }

        &[data-is-selected='true'],
        &:focus-visible,
        &:focus-within,
        &:has([data-state='open']),
        &:has([data-popover-state='open']) {
            ${TABLE_ROW_BG_COLOR_VAR}: ${vars.colors.surface.itemHover};
        }

        ${props =>
            props.$isClickable
                ? css`
                      &:hover {
                          ${TABLE_ROW_BG_COLOR_VAR}: ${vars.colors.surface
                              .itemHover};
                      }
                  `
                : css`
                      cursor: default;
                  `}
    }
`;

HBTableRow.TR = String(TR);
